/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  CostCenterFinderListItemDto,
  OfficeMeasurement,
  OfficeMeasurementDefault,
  SystemVarsEstimatingDto,
} from '@gh/builder-shared-data';
import {
  AutocompleteItemDto,
  AutocompleteItemWithDescription,
  ID,
  withDisplayAsNameAndDescription,
} from '@gh/common-shared-data';
import { mergeWithPrivelegedTokens, WithOptimisticLock, WithPrivilegedToken } from '@gh/core-data';
import { omit, pick } from 'lodash';

export type SystemVarsEstimatingCommon = WithPrivilegedToken<{
  measurementScale: OfficeMeasurement;
  measurementDefault: OfficeMeasurementDefault;
  originalItemsLocked: boolean;
  itemsLoaded: Date;
  allowBulkUpdateDifferentUom: boolean;
  cloneHousePlanFromJob: boolean;
  deleteHousePlanAfterDays: number;
  lastPublishedDate: Date;
  officeId: number;
  housePlan?: AutocompleteItemWithDescription;
}>;
export type SystemVarsEstimatingOrders = WithPrivilegedToken<{
  costCenter: CostCenterFinderListItemDto;
  showDeliveryDate: boolean;
  whenCombining: boolean;
  noShowLogo: boolean;
  showFrClause: boolean;
  allowSupplierChange: boolean;
  printRecordForm: boolean;
  criteriaEstimate: boolean;
  criteriaCouncil: boolean;
  criteriaFinance: boolean;
  criteriaColorSelection: boolean;
  criteriaJobSchedule: boolean;
  criteriaContract: boolean;
  criteriaJobProceeding: boolean;
  criteriaInsurancePolicy: boolean;
  priceBook?: AutocompleteItemDto;
  ordersMemo: string;
  ordersSendBackReceipt: boolean;
  ordersPlusRequiredText: string;
}>;

export type SystemVarsEstimating = WithOptimisticLock<WithPrivilegedToken<{
  id: ID;
  common: SystemVarsEstimatingCommon;
  orders: SystemVarsEstimatingOrders;
}>>;

export function systemVarsEstimateFromDto(dto: SystemVarsEstimatingDto): SystemVarsEstimating {
  return <SystemVarsEstimating>{
    ...pick(dto, 'id', 'etag', 'privilegedTokens'),
    common: {
      ...<SystemVarsEstimatingCommon>pick(dto, ['measurementScale', 'measurementDefault', 'originalItemsLocked',
        'itemsLoaded', 'allowBulkUpdateDifferentUom', 'cloneHousePlanFromJob', 'priceBook',
        'deleteHousePlanAfterDays', 'lastPublishedDate', 'officeId', 'orders']),
      housePlan: dto.housePlan && withDisplayAsNameAndDescription(dto.housePlan),
    },
    orders: pick(dto, ['costCenter', 'showDeliveryDate', 'whenCombining', 'noShowLogo', 'showFrClause',
      'allowSupplierChange', 'printRecordForm', 'criteriaEstimate', 'criteriaCouncil', 'criteriaFinance',
      'criteriaColorSelection', 'criteriaJobSchedule', 'criteriaContract', 'criteriaJobProceeding',
      'criteriaInsurancePolicy', 'ordersMemo', 'ordersSendBackReceipt', 'ordersPlusRequiredText']),
  };
}

export function systemVarsEstimateToDto(model: SystemVarsEstimating): SystemVarsEstimatingDto {
  return <SystemVarsEstimatingDto>{
    ...omit(model.common, 'appliesBuilderLicenceNo'),
    ...model.orders,
    costCenter: model.orders.costCenter && omit(model.orders.costCenter, ['suppliers', 'etag']),
    id: model.id,
    etag: model.etag,
    privilegedTokens: mergeWithPrivelegedTokens(model.common, model.orders),
  };
}
